import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { api, apiMethods } from '../services/api';
import { toast } from 'react-hot-toast';
import { playSuccessSound, playErrorSound, playWarningSound } from '../utils/sound';

interface Setup2FAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const Setup2FAModal: React.FC<Setup2FAModalProps> = ({ isOpen, onClose }) => {
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'init' | 'verify' | 'done' | 'already-enabled'>('init');
  const [loading, setLoading] = useState(false);

  const fetchQr = async () => {
    setLoading(true);
    try {
      const res: any = await apiMethods.post('/auth/2fa/setup', {});
      setQr(res.qr);
      setSecret(res.secret);
      setStep('verify');
    } catch (err: any) {
      if (err.status === 400 && err.details?.is2FAEnabled) {
        setStep('already-enabled');
        playWarningSound();
      } else {
        toast.error(err.message || 'Failed to generate QR');
        playErrorSound();
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiMethods.post('/auth/2fa/verify', { token: code });
      toast.success('2FA enabled successfully!');
      playSuccessSound();
      onClose();
    } catch (error) {
      toast.error('Invalid OTP. Please try again.');
      playErrorSound();
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setQr(null);
      setSecret(null);
      setCode('');
      setStep('init');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">Setup Google Authenticator (2FA)</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">✕</button>
        </div>
        {step === 'init' && (
          <button
            className="btn-primary w-full"
            onClick={fetchQr}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate QR Code'}
          </button>
        )}
        {step === 'verify' && qr && secret && (
          <>
            <div className="flex flex-col items-center mb-4">
              <img src={qr} alt="QR Code" className="mb-2 w-40 h-40" />
              <div className="text-xs text-gray-600 mb-2">Scan this QR in Google Authenticator</div>
              <div className="text-xs text-gray-500 break-all">Secret: <span className="font-mono">{secret}</span></div>
            </div>
            <form onSubmit={verifyCode} className="space-y-2">
              <input
                type="text"
                className="input-field w-full text-center"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={e => setCode(e.target.value)}
                maxLength={6}
                required
                pattern="[0-9]{6}"
                autoFocus
              />
              <button className="btn-primary w-full" type="submit" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>
            </form>
          </>
        )}
        {step === 'done' && (
          <div className="text-green-700 text-center font-semibold">
            2FA is now enabled for your account!<br />
            <button className="btn-primary mt-4" onClick={onClose}>Close</button>
          </div>
        )}
        {step === 'already-enabled' && (
          <div className="text-center">
            <div className="text-yellow-600 font-semibold mb-4">
              ⚠️ 2FA is already enabled for your account!
            </div>
            <p className="text-gray-600 mb-4">
              If you need to reset your 2FA settings, please contact the administrator.
            </p>
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Setup2FAModal; 